import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import { LoadingState } from '@/components/common/loading';
import { BootstrapErrorScreen } from '@/components/common/BootstrapErrorScreen';
import { useGeoMarkupStore } from '@/store';
import { ensureAppBootstrap, resetAppBootstrap } from '@/store/appBootstrap';
import {
  selectIsAuthenticated,
  selectWorkspaces,
} from '@/store/selectors/authSelectors';
import { useApplyUserPreferences, useSettingsSync } from '@/hooks';
import { getPendingInvitation } from '@/utils/pendingInvitation';
import { buildAuthorizeUrl } from '@/utils/authRedirect';
import { ROUTES } from './routes';

import styles from './ProtectedRoute.module.css';

interface ProtectedRouteProps {
  children: ReactNode;
  requireWorkspace?: boolean;
  redirectIfHasWorkspace?: boolean;
}

/**
 * Route guard that requires authentication and optionally a workspace.
 * Redirects to OAuth provider if not authenticated (confirmed 401).
 * Shows a retry screen on transient bootstrap failures (network, 503, etc.).
 * Redirects to onboarding if workspace required but none exist.
 * Redirects to map if on onboarding but already has workspaces.
 */
export function ProtectedRoute({
  children,
  requireWorkspace = true,
  redirectIfHasWorkspace = true,
}: ProtectedRouteProps) {
  const { t } = useTranslation();
  const isAuthenticated = useGeoMarkupStore(selectIsAuthenticated);
  const workspaces = useGeoMarkupStore(selectWorkspaces);
  const [ready, setReady] = useState(false);
  const [bootstrapError, setBootstrapError] = useState<unknown>(null);

  useApplyUserPreferences();
  useSettingsSync();

  // Single awaited bootstrap on mount: hydrates the store before rendering.
  // On confirmed 401 the promise resolves normally → isAuthenticated stays
  // false → OAuth redirect below. On transient errors the promise rejects →
  // we capture the error and show a retry screen instead of redirecting.
  useEffect(() => {
    ensureAppBootstrap().then(
      () => setReady(true),
      (error: unknown) => setBootstrapError(error)
    );
  }, []);

  const handleRetry = () => {
    setBootstrapError(null);
    setReady(false);
    resetAppBootstrap();
    ensureAppBootstrap().then(
      () => setReady(true),
      (error: unknown) => setBootstrapError(error)
    );
  };

  // Show loader while the bootstrap is in flight
  if (!ready && bootstrapError === null) {
    return (
      <div className={styles.loaderContainer}>
        <LoadingState centered size="xl" text={t('common.checkingAuth')} />
      </div>
    );
  }

  // Bootstrap failed with a transient error — show a retry screen.
  // Do NOT redirect to OAuth: authentication state is unknown.
  if (bootstrapError !== null) {
    return <BootstrapErrorScreen onRetry={handleRetry} />;
  }

  // Redirect to OAuth provider if not authenticated
  if (!isAuthenticated) {
    window.location.href = buildAuthorizeUrl(
      window.location.pathname + window.location.search
    );
    return null;
  }

  const hasWorkspaces = workspaces.length > 0;

  // Workspace routing logic
  if (requireWorkspace && !hasWorkspaces) {
    const pending = getPendingInvitation();
    if (pending) {
      return (
        <Navigate
          to={`/invite/${pending.token}?action=${pending.action}`}
          replace
        />
      );
    }
    // User needs workspace but doesn't have one → redirect to onboarding
    return <Navigate to={ROUTES.ONBOARDING} replace />;
  }

  const firstWorkspace = workspaces[0];
  if (!requireWorkspace && redirectIfHasWorkspace && firstWorkspace) {
    return <Navigate to={`/w/${firstWorkspace.shortId}/dashboard`} replace />;
  }

  return <>{children}</>;
}
